import {useEffect, useMemo, useState} from 'react'
import {Badge, Button, Col, FloatingLabel, Form, Offcanvas, Row, Spinner} from 'react-bootstrap'
import {FormPersonalLead} from '../FormPersonalLead'
import {FormComercialLead} from '../FormComercialLead'
import Select, {MultiValue} from 'react-select'
import {IResCatLeadSource} from '../../../leadSource/leadSourceTypes'
import {IResPipelines} from '../../../../types/pipelines'
import {IResTags} from '../../../../types/tags'
import {IResCampus} from '../../../../types/campus'
import {hasPermission, parseId} from '../../../../helpers'
import {IResAllListUsers, IResAllUsers} from '../../../../types/users'
import {ILeadData, IResUpdateLead, LeadByID} from '../../leadTypes'
import {IResAllReasons} from '../../../../types/reasonLoss'
import {useMutation} from '@apollo/client'
import {UPDATE_LEAD} from '../../../../gql/mutations/leadMutations'
import {toast} from 'react-toastify'
import {useGetUser} from '../../../../store/userStore'
import {ALL_PERMISSIONS} from '../../../../helpers/consts'
import {PhoneInput} from 'app/tfi/components/PhoneInput'
import {DateInput} from '../../../../components/DateInput'
import moment from 'moment'

interface IFormEditProps {
  dataLeadSource: IResCatLeadSource
  dataPipelines: IResPipelines
  dataTags: IResTags
  dataCampus: IResCampus
  dataUserList: IResAllListUsers
  onClose: () => void
  updateListLeads: () => void
  lead: LeadByID
  dataReasonLoss: IResAllReasons
  children: JSX.Element
}

export const FormEditLead = ({
  dataCampus,
  dataLeadSource,
  dataPipelines,
  dataTags,
  dataUserList,
  onClose,
  lead,
  dataReasonLoss,
  updateListLeads,
  children,
}: IFormEditProps) => {
  const permissions = useGetUser((s) => s.user?.permissions)
  const [gqlEdit, {data: dataEdit, loading: loadingEdit, error: errorEdit}] =
    useMutation<IResUpdateLead>(UPDATE_LEAD)

  const [leadType, setLeadType] = useState<'PERSONAL' | 'COMERCIAL'>('PERSONAL')
  const [valuePipelines, setValuePipelines] = useState('')
  const [stageValue, setStageValue] = useState('')
  const [birthDay, setBirthDay] = useState<string>('')
  const [valueTags, setValueTags] = useState<
    MultiValue<{
      value: string
      label: string
      color: string
    }>
  >([])
  const [valueMembers, setValueMembers] = useState<
    MultiValue<{
      value: string
      label: string
    }>
  >([])

  const marketplaceData = useMemo<ApplicationData | null>(() => {
    const metadata = lead?.marketplaceMetadata

    if (!metadata) return null
// console.log(' marketplace metadata', metadata);
    try {
      if (typeof metadata === 'string') {
        const parsed = JSON.parse(metadata)
        // console.log('parsed marketplace metadata', parsed);
        if (parsed && typeof parsed === 'object') {
          return parsed as ApplicationData
        }
        return null
      }
      return metadata as ApplicationData
    } catch (error) {
      console.warn('Unable to parse marketplace metadata', error)
      return null
    }
  }, [lead?.marketplaceMetadata])

  useEffect(() => {
    if (!lead) return
    const tLead = lead.comercialLead ? 'COMERCIAL' : lead.personalLead ? 'PERSONAL' : null
    if (!tLead) return
    setLeadType(tLead)
    const tags = lead.tagInsurance.edges.map(({node}) => ({
      value: node.id,
      label: node.name,
      color: node.color,
    }))
    setValueTags(tags)
    setValuePipelines(`${parseId(lead.stagePipeline.pipeline.id)}`)
    const mems = lead.user.edges.map(({node}) => ({
      value: node.id,
      label: `${node.firstName} ${node.lastName}`,
    }))
    setValueMembers(mems)
    setStageValue(`${parseId(lead.stagePipeline.id)}`)
    if (lead.personalLead?.birthDay) {
      setBirthDay(lead.personalLead.birthDay)
    }
  }, [lead])

  const defaultCountryCode = useMemo(() => {
    if (!lead) return '1'
    
    // Check personal lead country
    const personalCountry = lead.personalLead?.country?.toLowerCase() || ''
    if (personalCountry.includes('panama') || personalCountry.includes('panamá') || personalCountry === 'pa') return '507'
    if (personalCountry.includes('mexico') || personalCountry.includes('méxico') || personalCountry === 'mx') return '52'
    if (personalCountry.includes('colombia') || personalCountry === 'co') return '57'
    
    // Check campus name as a fallback hint
    const campusName = lead.campus?.name?.toLowerCase() || ''
    if (campusName.includes('panama') || campusName.includes('panamá')) return '507'
    
    return '1'
  }, [lead])

  useEffect(() => {
    if (errorEdit) toast.error(`Error: ${errorEdit.message}`)
  }, [errorEdit])

  useEffect(() => {
    if (!dataEdit) return
    if (dataEdit.updateLead.success) {
      toast.success('Lead updated successfully')
      onClose()
      updateListLeads()
    } else if (dataEdit.updateLead.errors) {
      toast.error(`Error: ${dataEdit.updateLead.errors?.[0]?.message || 'something happened'}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataEdit])

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const {
      name,
      email,
      phone,
      leadSource,
      pipeline,
      campus,
      reasonLeadLoss,
      tags,
      observations,
      stagePipeline,
      ...rest
    } = Object.fromEntries(new FormData(e.currentTarget))
    const leadData: ILeadData = {
      name,
      email,
      phone,
      typeLead: leadType,
      leadSource,
      pipeline,
      campus,
      reasonLeadLoss: reasonLeadLoss.toString() ? reasonLeadLoss : undefined,
      tags,
      observations,
      stagePipeline,
    }
    if (valueTags.length > 0) {
      leadData.tagInsurance = valueTags.map(({value}) => parseId(value))
    }
    if (valueMembers.length > 0) {
      leadData.user = valueMembers.map(({value}) => parseId(value))
    }
    if (leadType === 'PERSONAL') {
      leadData.personalLead = {...rest}
      if (!leadData.personalLead.birthDay) {
        delete leadData.personalLead.birthDay
      }
      delete leadData.comercialLead
    }
    if (leadType === 'COMERCIAL') {
      leadData.comercialLead = {...rest}
      delete leadData.personalLead
    }
    delete leadData.tags
    delete leadData.pipeline
    console.log('leadData', leadData)
    gqlEdit({
      variables: {
        id: lead.id,
        leadData,
      },
    })
  }

  return (
    <>
      <Offcanvas.Header closeButton className='bg-light'>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <Offcanvas.Title className='d-flex align-items-center gap-4'>{lead.name}</Offcanvas.Title>
          <div style={{flex: 0}}>
            <Badge bg='success' pill className='fs-2 d-flex align-items-center gap-1'>
              <i className='bi bi-telephone-plus' style={{color: 'inherit'}} />
              {lead.callCounter}
            </Badge>
          </div>
          <div className='d-flex align-items-center gap-1'>
            <button
              className=''
              onClick={() => {
                setLeadType('PERSONAL')
              }}
              style={{
                border: 'none',
                padding: '4px 10px',
                borderRadius: '14px',
                color: leadType === 'PERSONAL' ? 'white' : 'gray',
                backgroundColor: leadType === 'PERSONAL' ? '#3e97ff' : '#e4e4e4',
              }}
            >
              Personal
            </button>
            <button
              className=''
              onClick={() => {
                setLeadType('COMERCIAL')
              }}
              style={{
                border: 'none',
                padding: '4px 10px',
                borderRadius: '14px',
                color: leadType === 'COMERCIAL' ? 'white' : 'gray',
                backgroundColor: leadType === 'COMERCIAL' ? '#3e97ff' : '#e4e4e4',
              }}
            >
              Commercial
            </button>
          </div>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {children}
        <form onSubmit={handleSubmit}>
          <div className='lead-summary-layout'>
            <div className='lead-summary lead-summary-sticky'>
              <div className='kt-heading kt-heading--sm custom-border-brand my-3'>Lead Summary</div>
              <Form.Group className='mb-3'>
                <Form.Label>
                  Pipeline <span style={{color: 'red'}}>*</span>
                </Form.Label>
                <Form.Select
                  aria-label='Pipeline'
                  name='pipeline'
                  required
                  size='sm'
                  value={valuePipelines}
                  onChange={(e) => setValuePipelines(e.target.value)}
                >
                  <option value=''>Nothing selected</option>
                  {dataPipelines.allPipelines.edges.map(({node}) => (
                    <option value={parseId(node.id)} key={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  Stage <span style={{color: 'red'}}>*</span>
                </Form.Label>
                <Form.Select
                  aria-label='stagePipeline'
                  name='stagePipeline'
                  required
                  size='sm'
                  value={stageValue}
                  onChange={(e) => setStageValue(e.target.value)}
                >
                  <option value=''>Nothing selected</option>
                  {dataPipelines.allPipelines.edges
                    .find(({node}) => parseId(node.id) === valuePipelines)
                    ?.node.stagePipelineSet.edges.map(({node}) => (
                      <option key={node.id} value={parseId(node.id)}>
                        {node.name}
                      </option>
                    ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  Source <span style={{color: 'red'}}>*</span>
                </Form.Label>
                <Form.Select
                  aria-label='Lead source'
                  name='leadSource'
                  required
                  size='sm'
                  defaultValue={parseId(lead.leadSource.id)}
                >
                  <option value=''>Please select</option>
                  {dataLeadSource.allCategoryLeadSource.edges.map(({node}) => (
                    <optgroup label={node.name} key={node.id}>
                      {node.leadSourceSet.edges.map(({node: nodeLead}) => (
                        <option key={nodeLead.id} value={parseId(nodeLead.id)}>
                          {nodeLead.name}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3 z-999'>
                <Form.Label>
                  Tags <span style={{color: 'red'}}>*</span>
                </Form.Label>
                <Select
                  classNames={{
                    control: () => 'h-100',
                    container: () => 'h-100',
                  }}
                  placeholder='Select tags'
                  id='tags-cont'
                  required
                  inputId='tags'
                  name='tags'
                  options={dataTags.allTagInsurances.edges.map(({node}) => ({
                    value: node.id,
                    label: node.name,
                    color: node.color,
                  }))}
                  isMulti
                  value={valueTags}
                  onChange={(e) => {
                    setValueTags(e)
                  }}
                  styles={{
                    option: (styles, {data, isDisabled, isFocused, isSelected}) => {
                      const color = data.color
                      return {
                        ...styles,
                        zIndex: 9999,
                        backgroundColor: isDisabled
                          ? undefined
                          : isSelected
                          ? data.color
                          : isFocused
                          ? color
                          : undefined,
                        color: isDisabled ? '#ccc' : isSelected ? 'white' : 'black',
                        cursor: isDisabled ? 'not-allowed' : 'default',
                        ':active': {
                          ...styles[':active'],
                          backgroundColor: !isDisabled
                            ? isSelected
                              ? data.color
                              : color
                            : undefined,
                        },
                      }
                    },
                    multiValue: (styles, {data}) => {
                      const color = data.color
                      return {
                        ...styles,
                        backgroundColor: color,
                      }
                    },
                    multiValueLabel: (styles) => ({
                      ...styles,
                      color: 'white',
                    }),
                    multiValueRemove: (styles, {data}) => ({
                      ...styles,
                      color: 'white',
                      ':hover': {
                        backgroundColor: data.color,
                        color: 'white',
                      },
                    }),
                    control: (styles, {isDisabled, isFocused}) => {
                      return {
                        ...styles,
                        minHeight: '36px',
                        border: isDisabled
                          ? '1px solid #ccc'
                          : isFocused
                          ? '1px solid white'
                          : '1px solid #dbdfe9',
                        cursor: isDisabled ? 'not-allowed' : 'default',
                      }
                    },
                  }}
                />
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  Reason for Loss <span style={{color: 'red'}}>*</span>
                </Form.Label>
                <Form.Select
                  aria-label='Reason for loss'
                  name='reasonLeadLoss'
                  size='sm'
                  defaultValue={
                    lead?.reasonLeadLoss?.id ? parseId(lead.reasonLeadLoss.id) : undefined
                  }
                >
                  <option value=''>Nothing selected</option>
                  {dataReasonLoss.allReasonLeadLoss.edges.map(({node}) => (
                    <option value={parseId(node.id)} key={node.id}>
                      {node.reason}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3'>
                <Form.Label>
                  Location <span style={{color: 'red'}}>*</span>
                </Form.Label>
                <Form.Select
                  aria-label='Location'
                  name='campus'
                  required
                  size='sm'
                  defaultValue={parseId(lead.campus.id)}
                >
                  {dataCampus.allCampus.edges.map(({node}) => (
                    <option value={parseId(node.id)} key={node.id}>
                      {node.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className='mb-3 z-999'>
                <Form.Label>Assigned to</Form.Label>
                <Select
                  classNames={{
                    container: () => 'h-100',
                  }}
                  styles={{
                    control: (styles) => ({
                      ...styles,
                      minHeight: '36px',
                    }),
                  }}
                  placeholder='Select members'
                  options={dataUserList.allUsersList.map((node) => ({
                    value: node.id,
                    label: `${node.firstName} ${node.lastName}`,
                  }))}
                  isMulti
                  value={valueMembers}
                  onChange={(e) => {
                    setValueMembers(e)
                  }}
                />
              </Form.Group>
              <div className='kt-widget__info'>
                <span className='kt-widget__label'>Created at:</span>
                <span className='kt-widget__data'>
                  {lead.created ? moment(lead.created).format('DD MMM YYYY, hh:mm A') : 'N/R'}
                </span>
              </div>
              <div className='kt-widget__info'>
                <span className='kt-widget__label'>Last update:</span>
                <span className='kt-widget__data'>
                  {lead.updated ? moment(lead.updated).format('DD MMM YYYY, hh:mm A') : 'N/R'}
                </span>
              </div>
            </div>
            <div className='lead-summary-content'>
              <Row>
                {/* Personal Information | Address Information */}
                <Col className='col-lg-8 col-md-8 col-12 holder-division'>
                  <div className='kt-heading kt-heading--md custom-border-brand mb-3'>Personal Information</div>
                  <Row>
                    {/* Lead Name | Email | Civil Status */}
                    <Col className='col-12 mb-4'>
                      <Form.Group>
                        <Form.Label>
                          Lead Name <span style={{color: 'red'}}>*</span>
                        </Form.Label>
                        <Form.Control
                          placeholder='Lead Name'
                          name='name'
                          required
                          defaultValue={lead.name}
                        />
                      </Form.Group>
                    </Col>
                    {leadType === 'PERSONAL' && (
                      <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                        <DateInput
                          id='birthDay'
                          name='birthDay'
                          label={
                            <>
                              <span>Date of Birth</span>
                              <span className='age'>
                                {birthDay
                                  ? `Age: ${moment().diff(moment(birthDay), 'years')}`
                                  : 'Age: -'}
                              </span>
                            </>
                          }
                          labelClassName='label-date-of-birth-age d-flex justify-content-between align-items-center w-100'
                          placeholder='Date of Birth'
                          value={birthDay}
                          onChange={(_, value) => setBirthDay(value || '')}
                        />
                        <input type='hidden' name='birthDay' value={birthDay} />
                      </Col>
                    )}
                    {leadType === 'PERSONAL' && (
                      <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                        <Form.Group>
                          <Form.Label>Civil Status</Form.Label>
                          <Form.Control
                            name='civilStatus'
                            type='text'
                            placeholder='Civil Status'
                            defaultValue={lead.personalLead?.civilStatus || undefined}
                          />
                        </Form.Group>
                      </Col>
                    )}

                    {/* Email | Phone */}
                    <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                      <Form.Group>
                        <Form.Label>
                          Email <span style={{color: 'red'}}>*</span>
                        </Form.Label>
                        <Form.Control
                          placeholder='Email'
                          name='email'
                          required
                          defaultValue={lead.email}
                        />
                      </Form.Group>
                    </Col>
                    <Col className='col-lg-6 col-md-6 col-12 mb-4'>
                      <Form.Group>
                        <PhoneInput
                          id='phone'
                          name='phone'
                          label='Phone'
                          value={lead.phone ?? ''}
                          defaultCountryCode={defaultCountryCode}
                          onChange={() => {}}
                        />
                      </Form.Group>
                    </Col>

                    <Col className='col-12 mb-4'>
                      <FloatingLabel controlId='observations' label='Observations'>
                        <Form.Control
                          defaultValue={lead?.observations || ''}
                          name='observations'
                          as='textarea'
                          rows={3}
                          placeholder='Observations'
                          className='h-100'
                        />
                      </FloatingLabel>
                    </Col>
                  </Row>
                </Col>

                {leadType === 'PERSONAL' && (
                  <Col className='col-lg-4 col-md-4 col-12'>
                    <div className='kt-heading kt-heading--md custom-border-brand mb-3'>Address Information</div>
                    <FormPersonalLead info={lead.personalLead} />
                  </Col>
                )}
                {leadType === 'COMERCIAL' && <FormComercialLead info={lead.comercialLead} />}
                {marketplaceData && marketplaceData !== null && (
                  <Col className='col-12 mb-4'>
                    {marketplaceData && (
                      <div className='alert alert-info d-flex flex-column gap-1 mb-4'>
                        <strong>Leads details</strong>
                        
                        {marketplaceData.household?.type && (
                          <span>
                           Tipo:&nbsp;{marketplaceData.household.type}
                          </span>
                        )}

                        {marketplaceData.plan?.selection?.planId && marketplaceData.plan?.selection?.name && (
                           <span>
                           Plan:&nbsp;
                           {marketplaceData.plan.selection.planId} -{' '}
                           {marketplaceData.plan.selection.name}
                         </span>
                        )}

                        {marketplaceData.household?.effectiveDate && (
                           <span>
                           Effective Date: {marketplaceData.household.effectiveDate}
                         </span>
                        )}

                        {marketplaceData.plan?.selection?.issuer && (
                          <span>Carrier: {marketplaceData.plan.selection.issuer}</span>
                        )}

                        {marketplaceData.members?.length > 0 && (
                          <div>
                            <strong>Members:</strong>
                            <ul className='mb-0'>
                              {marketplaceData.members.map((member, index) => (
                                <li key={index}>
                                  {member.firstName} {member.lastName} - {member.birthDate} -{' '}
                                  {member.gender}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {marketplaceData.property_type && (
                          <span>
                            <strong>Property Type:</strong> {marketplaceData.property_type}
                          </span>
                        )}
                        {marketplaceData.damage_type && (
                          <span>
                            <strong>Damage Type:</strong> {marketplaceData.damage_type}
                          </span>
                        )}
                      </div>
                    )}
                  </Col>
                )}
              </Row>

              <div
                className='d-flex align-items-center justify-content-end gap-2 mt-8 pt-3 pb-8'
                style={{borderTop: '1px solid #f1f1f1'}}
              >
                <Button type='button' variant='light' onClick={onClose}>
                  Cancel
                </Button>
                {permissions && hasPermission(permissions, ALL_PERMISSIONS.EDIT_LEAD) && (
                  <Button type='submit' disabled={loadingEdit}>
                    {loadingEdit && <Spinner animation='border' size='sm' />}
                    Update
                  </Button>
                )}
              </div>
            </div>
          </div>
        </form>
      </Offcanvas.Body>
    </>
  )
}
